import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Heart, Plus, Minus, Truck, RotateCcw, Shield, ChevronRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { products } from '../../data/products';
import ProductCard from '../ProductCard/ProductCard';
import { useWishlist } from '../../context/WishlistContext';
import './ProductDetail.css';

const ProductDetail = () => {
  const { id } = useParams();
  const product = products.find((p) => p.id === id);

  const [selectedColor, setSelectedColor] = useState(product?.colors[0] || { name: 'Default', hex: '#000000' });
  const [selectedSize, setSelectedSize] = useState(product?.sizes[0] || 'One Size');
  const [quantity, setQuantity] = useState(1);
  const { isInWishlist, toggleWishlist } = useWishlist();
const isWishlisted = isInWishlist(product.id);
  const { addToCart, openCart } = useCart();

  // Scroll to top when component mounts or product changes
useEffect(() => {
  window.scrollTo(0, 0);
}, [id]);

  if (!product) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <h1>Product Not Found</h1>
        <Link to="/shop">Back to Shop</Link>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity, selectedSize, selectedColor);
    openCart();
  };

  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(product.rating);
    for (let i = 0; i < 5; i++) {
      stars.push(i < fullStars ? <Star key={i} className="product-info-star" fill="#f5c518" /> : <Star key={i} className="product-info-star empty" />);
    }
    return stars;
  };

  const discountPercentage = product.salePrice ? Math.round(((product.price - product.salePrice) / product.price) * 100) : 0;
  const relatedProducts = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  return (
    <div className="product-detail">
      <nav className="product-breadcrumb">
        <Link to="/">Home</Link>
        <ChevronRight size={14} />
        <Link to="/shop">Shop</Link>
        <ChevronRight size={14} />
        <span>{product.name}</span>
      </nav>

      <div className="product-detail-container">
        <div className="product-gallery">
          <div className="product-gallery-thumbnails">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={`product-thumbnail ${i === 1 ? 'active' : ''}`}>
                <img src={product.image} alt={`${product.name} view ${i}`} />
              </div>
            ))}
          </div>
          <div className="product-gallery-main">
            <img src={product.image} alt={product.name} />
          </div>
        </div>

        <div className="product-info">
          <p className="product-info-brand">{product.brand}</p>
          <h1 className="product-info-name">{product.name}</h1>

          <div className="product-info-rating">
            <div className="product-info-stars">{renderStars()}</div>
            <span className="product-info-reviews-link">{product.reviews.toLocaleString()} Reviews</span>
          </div>

          <div className="product-info-pricing">
            <span className="product-info-price">${(product.salePrice || product.price).toFixed(2)}</span>
            {product.salePrice && (
              <>
                <span className="product-info-original-price">${product.price.toFixed(2)}</span>
                <span className="product-info-discount">{discountPercentage}% OFF</span>
              </>
            )}
          </div>

          {product.colors.length > 0 && (
            <div className="product-colors">
              <p className="product-colors-label">Color: <span>{selectedColor.name}</span></p>
              <div className="product-colors-options">
                {product.colors.map((color) => (
                  <button key={color.name} className={`product-color-option ${selectedColor.name === color.name ? 'selected' : ''}`} style={{ backgroundColor: color.hex }} onClick={() => setSelectedColor(color)} title={color.name} />
                ))}
              </div>
            </div>
          )}

          <div className="product-sizes">
            <div className="product-sizes-header">
              <p className="product-sizes-label">Size: <span>{selectedSize}</span></p>
            </div>
            <div className="product-sizes-options">
              {product.sizes.map((size) => (
                <button key={size} className={`product-size-option ${selectedSize === size ? 'selected' : ''}`} onClick={() => setSelectedSize(size)}>{size}</button>
              ))}
            </div>
          </div>

          <div className="product-quantity">
            <p className="product-quantity-label">Quantity</p>
            <div className="product-quantity-selector">
              <button className="product-quantity-btn" onClick={() => setQuantity(Math.max(1, quantity - 1))}><Minus size={16} /></button>
              <span className="product-quantity-value">{quantity}</span>
              <button className="product-quantity-btn" onClick={() => setQuantity(quantity + 1)}><Plus size={16} /></button>
            </div>
          </div>

          <div className="product-actions">
            <button className="product-add-to-cart" onClick={handleAddToCart}>Add to Bag</button>
            <button className="product-add-to-wishlist" onClick={() => toggleWishlist(product)}>
              <Heart size={18} fill={isWishlisted ? '#e21a2c' : 'transparent'} color={isWishlisted ? '#e21a2c' : 'currentColor'} />
              {isWishlisted ? 'Added to Wishlist' : 'Add to Wishlist'}
            </button>
          </div>

          <div className="product-features">
            <div className="product-feature"><Truck size={20} /> Free Shipping over $49</div>
            <div className="product-feature"><RotateCcw size={20} /> Easy Returns</div>
            <div className="product-feature"><Shield size={20} /> Secure Checkout</div>
          </div>

          <div className="product-description">
            <h3 className="product-description-title">Product Details</h3>
            <p className="product-description-text">{product.description}</p>
          </div>
        </div>
      </div>

      {relatedProducts.length > 0 && (
        <div className="related-products">
          <h2 className="related-products-title">You May Also Like</h2>
          <div className="related-products-grid">
            {relatedProducts.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
